/**
 * Action Items Sidebar
 * 
 * Global action items sidebar - shows all action items from all agent conversations.
 * Not conversation-specific - items persist across agents.
 */

import React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatTodoList } from './ChatTodoList';
import { ConversationTodoItem, ConversationTodoList } from '../types/chat';
import { cn } from '@flowstate/utils';

interface ActionItemsSidebarProps {
  items: ConversationTodoItem[];
  onUpdate: (todoList: ConversationTodoList) => void;
  onClose: () => void;
  isMobile?: boolean;
  onExtractFromChat?: () => void;
  onReplaceFromChat?: () => void;
}

export const ActionItemsSidebar: React.FC<ActionItemsSidebarProps> = ({
  items,
  onUpdate,
  onClose,
  isMobile = false,
  onExtractFromChat,
  onReplaceFromChat
}) => {
  // Create a temporary todo list structure for the ChatTodoList component
  const todoList: ConversationTodoList = {
    id: 'global-action-items',
    title: 'Action Items',
    items,
    context: 'Global action items from all agents',
    createdAt: new Date().toISOString()
  };

  return (
    <div className={cn(
      "bg-muted/30 backdrop-blur-sm border-l flex flex-col h-full",
      isMobile && "absolute inset-0 z-50 w-full"
    )}>
      {/* Header */}
      <div className="flex flex-col border-b bg-background">
        <div className="flex items-center justify-between p-4 pb-2">
          <div>
            <h2 className="text-lg font-semibold">Action Items</h2>
            <p className="text-xs text-muted-foreground">
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Manual Sync Controls */}
        <div className="flex gap-2 px-4 pb-4">
          {onExtractFromChat && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 text-xs h-7"
              onClick={onExtractFromChat}
            >
              Add New
            </Button>
          )}
          {onReplaceFromChat && (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 text-xs h-7"
              onClick={onReplaceFromChat}
            >
              Replace All
            </Button>
          )}
        </div>
      </div>

      {/* Action Items List */}
      <div className="flex-1 overflow-auto">
        {items.length > 0 ? (
          <ChatTodoList
            todoList={todoList}
            messageId="global-action-items"
            onUpdate={(updatedItems) => onUpdate({ ...todoList, items: updatedItems })}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-muted-foreground"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-sm font-medium mb-1">No action items yet</h3>
            <p className="text-xs text-muted-foreground max-w-xs">
              Action items suggested by your AI agents will appear here
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionItemsSidebar;
